import { detectPlatform, isNewerVersion } from "@/Utils/version";
import { localApiPort } from "../lib/localApi";
import {
  downloadInstallerInApp,
  type InstallerDownloadOptions,
} from "./windowsInstallerDownload";

export interface UpdateManifest {
  application: { version: string | null };

  native: {
    version: string;

    fileName: string;

    publishedAt: string | null;
  } | null;
}

/** ¿Corremos dentro del shell Electron? (bridge `window.electronAPI`). */

export const isElectron = (): boolean =>
  typeof window !== "undefined" &&
  Boolean((window as unknown as { electronAPI?: unknown }).electronAPI);

/**
 * Primera versión de Windows cuyo updater integrado se puede usar sin riesgo.
 * TIENE que ser la versión que publica el arreglo del swap; si el release sale
 * con otro número, este valor se cambia con él.
 */
export const WINDOWS_SAFE_SHELL_VERSION = "0.8.30";

/**
 * Shells de Windows que tienen que actualizarse con el INSTALADOR (descargado
 * desde este modal) en vez de con su updater integrado.
 *
 * Esta pantalla la sirve la web, así que la decisión la toma código nuevo
 * aunque el shell instalado sea viejo: es el único punto donde se puede
 * proteger a alguien que ya tiene una versión con un updater roto.
 *
 * Tres motivos, acumulados: hasta 0.8.24 por el layout viejo; 0.8.25–0.8.28
 * porque su script de swap solo cerraba Eriol.exe y
 * `extension-agent.exe`/`livekit-server.exe` bloqueaban `resources\`
 * (arreglado en 0.8.29); y hasta 0.8.29 inclusive porque ese script mueve
 * TODO lo que haya en la carpeta de instalación a `.old` y la borra — quien
 * instaló en una carpeta compartida (D:\Juegos, Documentos) pierde ahí lo
 * ajeno. El instalador, en cambio, extrae encima y no borra nada.
 */
/** Una versión legible de verdad: solo dígitos y puntos ("0.8.30"). */
const VERSION_NUMERICA = /^\d+(?:\.\d+)*$/;

export const requiresWindowsInstaller = (version: string | null): boolean => {
  if (detectPlatform() !== "windows") return false;
  // Sin versión (getCurrentShellVersion devuelve null cuando el IPC falla), o con una que
  // no es una versión, no se puede AFIRMAR que el shell sea seguro: se asume lo inseguro
  // y se manda al instalador. `isNewerVersion` falla abierto —  lee "garbage.999" como
  // [0, 999] y contesta false— , así que el filtro tiene que estar acá, antes.
  if (typeof version !== "string" || !VERSION_NUMERICA.test(version.trim())) {
    return true;
  }
  return isNewerVersion(version.trim(), WINDOWS_SAFE_SHELL_VERSION);
};

export const downloadWindowsInstaller = async (
  options?: InstallerDownloadOptions,
) => {
  const backend = import.meta.env.VITE_BACKEND_URL || "http://localhost:3000";
  if (!isElectron())
    throw new Error("La recuperacion del instalador requiere Eriol Desktop.");
  return downloadInstallerInApp(String(backend), options);
};

export const WINDOWS_INSTALLER_RECOVERY_EVENT =
  "eriol:windows-installer-recovery";
export const showWindowsInstallerRecovery = (version: string): void => {
  window.dispatchEvent(
    new CustomEvent(WINDOWS_INSTALLER_RECOVERY_EVENT, { detail: { version } }),
  );
};

/**
 * Versión del shell nativo instalado (registro-verdad vía `app.getVersion()`).
 * `null` fuera de Electron o si el IPC falla.
 */

export const getCurrentShellVersion = async (): Promise<string | null> => {
  try {
    const ipc = (
      window as unknown as {
        electronAPI?: { getAppVersion?: () => Promise<string | null> };
      }
    ).electronAPI;

    const version = await ipc?.getAppVersion?.();

    // Preloads/handlers viejos devuelven el sentinel "Unknown" ante error.

    // Normalizamos a null: "no sé la versión" no es una versión y no debe

    // llegar a comparaciones ni a la UI como si lo fuera.

    return version && version !== "Unknown" ? version : null;
  } catch (err) {
    console.warn("[updater] electronAPI.getAppVersion() falló:", err);

    return null;
  }
};

/** Manifiesto combinado (web + nativo) desde el backend. `null` si no hay red. */

export const fetchUpdateManifest = async (): Promise<UpdateManifest | null> => {
  // Mismo fallback que services/api.ts — NUNCA VITE_BASE_URL (esa es la URL

  // del propio frontend: el fetch pegaría al SPA y devolvería HTML).

  const backendUrl =
    import.meta.env.VITE_BACKEND_URL || "http://localhost:3000";

  try {
    const response = await fetch(
      `${String(backendUrl).replace(/\/$/, "")}/updates/check?platform=${detectPlatform()}`,

      { method: "GET", headers: { "Content-Type": "application/json" } },
    );

    if (!response.ok) return null;

    return (await response.json()) as UpdateManifest;
  } catch (err) {
    console.warn("[updater] no se pudo consultar /updates/check:", err);

    return null;
  }
};

/**
 * Dispara el updater nativo standalone (updater.exe) vía el server HTTP local
 * de Electron. La app se cierra para completar la instalación. Mismo endpoint
 * que usa el panel About.
 */

export const triggerNativeInstall = async (): Promise<void> => {
  if (requiresWindowsInstaller(await getCurrentShellVersion())) {
    await downloadWindowsInstaller();
    return;
  }
  const apiPort = localApiPort();

  // Token por-boot del shell: el endpoint local lo exige (header

  // x-eriol-updater-token) para que una página web arbitraria no pueda lanzar

  // updater.exe. Shells viejos no exponen el método —tampoco validan el

  // header—, así que sin token mandamos el POST igual y sigue funcionando.

  let updaterToken: string | null = null;

  try {
    updaterToken = (await window.electronAPI?.getUpdaterToken?.()) ?? null;
  } catch (err) {
    console.warn("[updater] electronAPI.getUpdaterToken() falló:", err);
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (updaterToken) {
    headers["x-eriol-updater-token"] = updaterToken;
  }

  const response = await fetch(
    `http://localhost:${apiPort}/api/updater/download-and-install`,

    {
      method: "POST",

      headers,

      body: JSON.stringify({}),
    },
  );

  // Body consumido UNA vez y usado para ambos caminos de error: un 401 del
  // middleware de token (u otro error HTTP con JSON) llega con su mensaje
  // real en vez de un "HTTP <status>" opaco.
  const raw = await response.text();

  let data: { success?: boolean; error?: string } = {};

  try {
    data = JSON.parse(raw) as { success?: boolean; error?: string };
  } catch {
    // Body no-JSON: se cae a los mensajes genéricos.
  }

  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  if (!data.success) {
    throw new Error(data.error || "Updater did not return success");
  }
};
